<template>
  <Dialog :visible="manageSkillGroupingSkillsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ manageSkillGroupingTitle }} Agent Skills
    </template>

    <div class="full-width flex">
      <Dropdown :options="availableSkills"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedSkill"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addSkillToGroupingSubmitting" @click="addSkillToGrouping(selectedSkill)" :disabled="!selectedSkill" label="Add Agent Skill"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="skillGroupingSkills">
        <Column field="name" header="Name"></Column>
        <Column field="shortDescription" header="Description"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteSkillGroupingSkill(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeSkillFromGroupingSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageSkillGroupingDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageSkillGroupingId ? 'Edit' : 'Add' }} Agent Skill Grouping
    </template>

    <div class="form-element">
      Title:
      <InputText v-model="manageSkillGroupingTitle" :invalid="manageSkillGroupingTitleInvalid" class="form-element full-width" type="text"
                 @change="manageSkillGroupingTitleChange"/>
      <div v-if="manageSkillGroupingTitleInvalid">
        <InlineMessage severity="error">Title is required.</InlineMessage>
      </div>
    </div>

    <div class="form-element">
      Machine Name:
      <InputText v-model="manageSkillGroupingMachineName" :invalid="manageSkillGroupingMachineNameInvalid" class="form-element full-width" type="text"
                 @change="manageSkillGroupingMachineNameChange"/>
      <div v-if="manageSkillGroupingMachineNameInvalid">
        <InlineMessage severity="error">Machine Name is required.</InlineMessage>
      </div>
    </div>

    <div class="form-element">
      Grouping Order:
      <InputNumber v-model="manageSkillGroupingOrder" :invalid="manageSkillGroupingOrderInvalid" class="form-element full-width"
                   @update:modelValue="manageSkillGroupingOrderChange"/>
      <div v-if="manageSkillGroupingOrderInvalid">
        <InlineMessage severity="error">{{ manageSkillGroupingOrderErrorMessage }}</InlineMessage>
      </div>
    </div>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageSkillGroupingSubmitting" :loading="manageSkillGroupingSubmitting" label="Save" type="button" @click="saveSkillGrouping"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="skillGroupingsLoading || skillsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">Agent Skill Groupings</h2>
        <div class="align-right mr">
          <Button @click="manageSkillGrouping(null)"><i class="pi pi-plus"></i> Add Agent Skill Grouping</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="sortedSkillGroupings">
          <Column field="title" header="Title"></Column>
          <Column field="machineName" header="Machine Name"></Column>
          <Column field="groupingOrder" header="Order"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="info" @click="manageSkillGrouping(slotProps.data)" label="Edit"></Button>
              <Button class="ml" severity="info" @click="manageSkillGroupingSkills(slotProps.data)" label="Manage Agent Skills"></Button>
              <Button class="ml" severity="danger" @click="deleteSkillGrouping(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { loadSkillGroupings, useSkillGroupings, useSkills, loadSkills, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeSkillGrouping, decodeSkillGroupingAssignment } from "../util/util.js";

const { skillGroupings, skillGroupingAssignments, skillGroupingsLoading } = useSkillGroupings()
const { skills, skillsLoading } = useSkills()
const { hasAdminAccess } = useUsers()

const toast = useToast()
const confirm = useConfirm()

async function deleteSkillGrouping(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.title + '?',
    header: 'Confirm Delete Agent Skill Grouping',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        skillGroupingsLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-skill-groupings/' + data.id)
        if (request.id) {
          skillGroupings.value = skillGroupings.value.filter(q => String(q.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        skillGroupingsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(skillGroupingsLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

// Load additional data when component loads
watch(selectedAccount, async (newValue) => {
  if (newValue) {
    await Promise.all([
      loadSkillGroupings(),
      loadSkills()
    ])
  }
}, { immediate: true })

const manageSkillGroupingDialogVisible = ref(false)
const manageSkillGroupingSubmitting = ref(false)

const manageSkillGroupingSkillsDialogVisible = ref(false)
const addSkillToGroupingSubmitting = ref(false)
const removeSkillFromGroupingSubmitting = ref(false)
const selectedSkill = ref(null)

const manageSkillGroupingId = ref(null)
const manageSkillGroupingTitle = ref(null)
const manageSkillGroupingMachineName = ref(null)
const manageSkillGroupingOrder = ref(null)

const manageSkillGroupingTitleInvalid = ref(false)
const manageSkillGroupingMachineNameInvalid = ref(false)
const manageSkillGroupingOrderInvalid = ref(false)
const manageSkillGroupingOrderErrorMessage = ref('')

function manageSkillGroupingTitleChange() {
  manageSkillGroupingTitleInvalid.value = !manageSkillGroupingTitle.value
}

function manageSkillGroupingMachineNameChange() {
  manageSkillGroupingMachineNameInvalid.value = !manageSkillGroupingMachineName.value
}

function manageSkillGroupingOrderChange() {
  manageSkillGroupingOrderInvalid.value = manageSkillGroupingOrder.value === null || manageSkillGroupingOrder.value === undefined
  manageSkillGroupingOrderErrorMessage.value = ''
}

function manageSkillGrouping(data) {
  manageSkillGroupingDialogVisible.value = true
  manageSkillGroupingId.value = data?.id
  manageSkillGroupingTitle.value = data?.title
  manageSkillGroupingMachineName.value = data?.machineName
  manageSkillGroupingOrder.value = data?.groupingOrder
}

function manageSkillGroupingSkills(data) {
  manageSkillGroupingSkillsDialogVisible.value = true
  manageSkillGroupingId.value = data?.id
  manageSkillGroupingTitle.value = data?.title
}

function handleClose() {
  manageSkillGroupingId.value = null
  manageSkillGroupingTitle.value = null
  manageSkillGroupingMachineName.value = null
  manageSkillGroupingOrder.value = null

  manageSkillGroupingTitleInvalid.value = false
  manageSkillGroupingMachineNameInvalid.value = false
  manageSkillGroupingOrderInvalid.value = false
  manageSkillGroupingOrderErrorMessage.value = ''

  manageSkillGroupingSubmitting.value = false
  manageSkillGroupingDialogVisible.value = false

  manageSkillGroupingSkillsDialogVisible.value = false
  addSkillToGroupingSubmitting.value = false
  removeSkillFromGroupingSubmitting.value = false
  selectedSkill.value = null
}

function handleError(e) {
  console.log(e)
  addSkillToGroupingSubmitting.value = false
  manageSkillGroupingSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageSkillGroupingTitleInvalid.value = false
  manageSkillGroupingMachineNameInvalid.value = false
  manageSkillGroupingOrderInvalid.value = false
  manageSkillGroupingOrderErrorMessage.value = ''
}

async function saveSkillGrouping() {
  let valid = true
  resetValidations()
  if (!manageSkillGroupingTitle.value) {
    manageSkillGroupingTitleInvalid.value = true
    valid = false
  }
  if (!manageSkillGroupingMachineName.value) {
    manageSkillGroupingMachineNameInvalid.value = true
    valid = false
  }
  if (manageSkillGroupingOrder.value === null || manageSkillGroupingOrder.value === undefined) {
    manageSkillGroupingOrderInvalid.value = true
    manageSkillGroupingOrderErrorMessage.value = 'Grouping Order is required.'
    valid = false
  } else {
    // Check for duplicate grouping order
    const duplicateGrouping = skillGroupings.value.find(sg => 
      sg.groupingOrder === manageSkillGroupingOrder.value && 
      String(sg.id) !== String(manageSkillGroupingId.value)
    )
    if (duplicateGrouping) {
      manageSkillGroupingOrderInvalid.value = true
      manageSkillGroupingOrderErrorMessage.value = `Grouping Order must be unique. "${duplicateGrouping.title}" already uses this Grouping Order.`
      valid = false
    }
  }

  if (!valid) {
    return false
  }

  if (manageSkillGroupingId.value) {
    const payload = {
      title: manageSkillGroupingTitle.value,
      machine_name: manageSkillGroupingMachineName.value,
      grouping_order: manageSkillGroupingOrder.value,
    }
    manageSkillGroupingSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/ai-skill-groupings/' + manageSkillGroupingId.value, payload)
      if (result.id) {
        for (let i = 0; i < skillGroupings.value.length; i++) {
          if (String(skillGroupings.value[i].id) === String(result.id)) {
            skillGroupings.value[i] = decodeSkillGrouping(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageSkillGroupingSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      title: manageSkillGroupingTitle.value,
      machine_name: manageSkillGroupingMachineName.value,
      grouping_order: manageSkillGroupingOrder.value,
      cephisso_platform_accounts_id: selectedAccount.value
    }
    manageSkillGroupingSubmitting.value = true
    try {
      const result = await post('/crbn-tech/ai-skill-groupings', payload)
      if (result.id) {
        skillGroupings.value.push(decodeSkillGrouping(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageSkillGroupingSubmitting.value = false
      handleError(e)
    }
  }
}

const sortedSkillGroupings = computed(() => {
  return skillGroupings.value.slice().sort((a, b) => (a.groupingOrder || 0) - (b.groupingOrder || 0))
})

const skillGroupingSkills = computed(() => {
  const output = []
  if (!manageSkillGroupingId.value) {
    return output
  }
  for (let i = 0; i < skillGroupingAssignments.value.length; i++) {
    if (String(skillGroupingAssignments.value[i].groupingId) === String(manageSkillGroupingId.value)) {
      const skill = skills.value.find(s => String(s.id) === String(skillGroupingAssignments.value[i].skillId))
      if (skill) {
        output.push({
          skillGroupingAssignmentId: skillGroupingAssignments.value[i].id,
          ...skill
        })
      }
    }
  }
  return output
})

const availableSkills = computed(() => skills.value.filter(s => !skillGroupingSkills.value?.find(a => String(a.id) === String(s.id))))

async function addSkillToGrouping(id) {
  const payload = {
    ai_skills_id: id,
    ai_skill_groupings_id: manageSkillGroupingId.value,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addSkillToGroupingSubmitting.value = true
  const request = await post('/crbn-tech/ai-skill-grouping-assignments', payload)
  if (request.id) {
    skillGroupingAssignments.value.push(decodeSkillGroupingAssignment(request))
  }
  else {
    handleError()
  }
  addSkillToGroupingSubmitting.value = false
  selectedSkill.value = null
}

async function deleteSkillGroupingSkill(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + manageSkillGroupingTitle.value + '?',
    header: 'Confirm Agent Skill Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeSkillFromGroupingSubmitting.value = true
        for (let i = 0; i < skillGroupingAssignments.value?.length; i++) {
          if (
              (String(skillGroupingAssignments.value[i].groupingId) === String(manageSkillGroupingId.value)) &&
              (String(skillGroupingAssignments.value[i].skillId) === String(data.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-skill-grouping-assignments/' + skillGroupingAssignments.value[i].id)
            if (request.id) {
              skillGroupingAssignments.value = skillGroupingAssignments.value.filter(sga => String(sga.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeSkillFromGroupingSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

</script>

<style scoped>
</style>




